import { lazy } from "react";

import { useRouteGate } from "@/hooks/useRouteGate";
import { RouteGateDenied, RouteGateLoading } from "@/components/routing/RouteGateFallback";
import { PageErrorBoundary } from "@/components/error-boundaries/ErrorBoundary";

const LabsStyleWrapper = lazy(() =>
  import("@/components/labs/LabsStyleWrapper").then((m) => ({ default: m.LabsStyleWrapper })),
);
const TalentAcquisition = lazy(() => import("@/pages/labs/TalentAcquisition"));

/**
 * /hiring in App.tsx is TalentAcquisition wrapped in <LabsStyleWrapper>. The
 * registry models "the page at this path", so the composition is kept as a
 * small wrapper around the same two lazy imports App.tsx used to declare
 * (same code-splitting, same modules). Split into its own file (rather than
 * defined inline in registry.tsx) so registry.tsx stays a pure-data module
 * for react-refresh purposes.
 *
 * Route-gated (RLS phase2-final, intern_manager task): recruitment access
 * mirrors the candidates/candidate_documents RLS policies — admin, manager,
 * or coach ("hiring" gate). Same wrapper pattern as Staff.tsx/Hub.tsx.
 */
export default function HiringPage(): JSX.Element {
  const { allowed, isLoading } = useRouteGate("hiring");

  if (isLoading) {
    return (
      <PageErrorBoundary>
        <RouteGateLoading />
      </PageErrorBoundary>
    );
  }

  if (!allowed) {
    return (
      <PageErrorBoundary>
        <RouteGateDenied
          title="Hiring"
          description="Recruitment is limited to managers, coaches, and admins."
        />
      </PageErrorBoundary>
    );
  }

  return (
    <LabsStyleWrapper>
      <TalentAcquisition />
    </LabsStyleWrapper>
  );
}
